import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { ReactKeycloakProvider } from "@react-keycloak/web";
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import MenuIcon from '@mui/icons-material/Menu';
import { keycloak, initOptions } from "./Keycloak";
import { ThemeProvider } from './components/helpers/ThemeContext';
import { useKeycloak } from '@react-keycloak/web';
import DrawerContent from './components/helpers/DrawerContent';
import HeaderBar from './components/helpers/HeaderBar';

import LandingPage from './components/pages/LandingPage';
import Impressum from './components/pages/Impressum';
import Documentation from './components/pages/Documentation';
import Home from './components/pages/Home';
import Dashboard from './components/pages/Dashboard';
import CloudManagement from './components/pages/CloudManagement';
import AppHub from './components/pages/AppHub';
import MeetingSummarizer from './components/apphub/MeetingSummarizer';
import NodeMonitoring from './components/cloudmanagement/NodeMonitoring';
import DiscoverInstances from './components/cloudmanagement/DiscoverInstances';
import Registration from './components/authentication/Registration';
import RegistrationInvite from './components/authentication/RegistrationInvite';
import PaaS from './components/pages/PaaS';
import Settings from './components/pages/Settings';
import Test from './components/pages/Test';
import HashCat from './components/apphub/HashCat';
import Callbot from './components/apphub/Callbot';
import PioWidget from './components/apphub/PioWidget';
import { KeyManager, RAGUpload, ManageAgents } from './components/AgentStudio/ManagingAgents';
import AgentStudio from './components/pages/AgentStudio';
import PrivateRoute from './components/authentication/PrivateRoute';
import ProtectedRoute from './components/authentication/ProtectedRoute';
import ChatAgent from './components/apphub/ChatAgent';
import AdminPage from './components/pages/AdminPage';
import { UserProvider, useUser } from './contexts/UserContext';
import ChatPage from './components/pioagent/ChatPage';
import ApiConfigPage from './components/pioagent/ApiConfigPage';
import { PioAgentUserProvider } from './contexts/PioAgentUserContext';


const drawerWidth = 240;
const collapsedDrawerWidth = 80;

function AppContentInner() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [language, setLanguage] = useState('en');
  const { keycloak, initialized: keycloakInitialized } = useKeycloak();
  const location = useLocation();
  const { onlyServiceRoleUser, loading: userLoading, userTypeDetermined } = useUser();

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleDrawerCollapseToggle = () => setIsCollapsed(!isCollapsed);

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'de' : 'en');
  };

  const isRegistrationInvitePage = location.pathname.includes('/user/v1/user/invitation/');
  const isLandingPage = !keycloak.authenticated && (location.pathname === '/' || location.pathname === '/impressum' || location.pathname === '/documentation');
  
  // Check if PioAgent is opened from AgentStudio (fullscreen mode)
  const isPioAgentFullscreen = location.pathname === '/pioagent/chat' && 
    sessionStorage.getItem('pioagent_agent_context') !== null;
  
  // Hide sidebar for onlyServiceRoleUser (but keep headerbar)
  // Only calculate this AFTER user type is determined
  const shouldHideSidebar = userTypeDetermined && onlyServiceRoleUser === true && !isPioAgentFullscreen;

  // Check if we need to redirect onlyServiceRoleUser to AgentStudio
  const needsRedirect = userTypeDetermined && onlyServiceRoleUser === true && keycloak.authenticated && location.pathname !== '/agent-studio' && !isPioAgentFullscreen;

  const getLandingPage = () => {
    return <LandingPage language={language} toggleLanguage={toggleLanguage} />;
  };

  // TEMPORARY DEBUG: Remove loading block for service users to prevent eternal loading
  // CRITICAL: Show loading state BEFORE rendering ANY navigation elements or routes
  // This prevents flash of wrong UI for onlyServiceRoleUser
  // Block rendering if:
  // 1. Keycloak is not initialized yet (wait for it), OR
  // 2. User is authenticated AND user type is not yet determined (wait for user type), OR
  // 3. We need to redirect onlyServiceRoleUser (prevent any rendering before redirect)
  // 4. AND we're not on a public page (landing/registration)
  const shouldShowLoading = (!keycloakInitialized || (keycloak.authenticated && !userTypeDetermined)) && !isRegistrationInvitePage && !isLandingPage;
  
  if (shouldShowLoading) {
    return (
      <ThemeProvider>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', width: '100vw', position: 'fixed', top: 0, left: 0, zIndex: 9999, bgcolor: 'background.default' }}>
          <CssBaseline />
          <CircularProgress />
        </Box>
      </ThemeProvider>
    );
  }

  // CRITICAL: Handle redirect AFTER loading check but BEFORE any component rendering
  // This prevents routes and navigation from rendering at all for onlyServiceRoleUser
  if (needsRedirect) {
    return <Navigate to="/agent-studio" replace />;
  }

  return (
    <ThemeProvider>
      <Box sx={{ display: 'flex', m: 0, p: 0 }}>
        <CssBaseline />
        {keycloak.authenticated && !isRegistrationInvitePage && !isPioAgentFullscreen && !shouldHideSidebar && !needsRedirect && userTypeDetermined && (
            <Box component="nav" sx={{ 
              width: { sm: isCollapsed ? collapsedDrawerWidth : drawerWidth }, 
              flexShrink: { sm: 0 },
              display: { xs: 'none', sm: 'block' }
            }}>
              <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{ keepMounted: true }}
                sx={{ 
                  display: { xs: 'block', sm: 'none' }, 
                  '& .MuiDrawer-paper': { 
                    boxSizing: 'border-box', 
                    width: drawerWidth,
                    maxWidth: 'vw'
                  }
                }}
              >
                <DrawerContent isCollapsed={false} onToggleCollapse={handleDrawerCollapseToggle} />
              </Drawer>
              <Drawer
                variant="permanent"
                sx={{ 
                  display: { xs: 'none', sm: 'block' }, 
                  '& .MuiDrawer-paper': { 
                    boxSizing: 'border-box', 
                    width: isCollapsed ? collapsedDrawerWidth : drawerWidth,
                    transition: 'width 0.3s ease'
                  }
                }}
                open
              >
                <DrawerContent isCollapsed={isCollapsed} onToggleCollapse={handleDrawerCollapseToggle} />
              </Drawer>
            </Box>
          )}

          <Box component="main" sx={{ 
            flexGrow: 1, 
            p: (isRegistrationInvitePage || isLandingPage || isPioAgentFullscreen || shouldHideSidebar) ? 0 : { xs: 1, sm: 2, md: 3 }, 
            pt: { xs: 0, sm: 0 }, 
            width: (keycloak.authenticated && !isRegistrationInvitePage && !isPioAgentFullscreen && !shouldHideSidebar) ? { 
              xs: '100%',
              sm: `calc(100% - ${isCollapsed ? collapsedDrawerWidth : drawerWidth}px)` 
            } : '100%',
            minHeight: '100vh',
            overflowX: 'hidden',
            transition: theme => theme.transitions.create(['width', 'margin'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.leavingScreen,
            }),
          }}>
            {keycloak.authenticated && !isRegistrationInvitePage && !isPioAgentFullscreen && userTypeDetermined && (
              <>
                <HeaderBar isCollapsed={isCollapsed} sidebarHidden={shouldHideSidebar} />
              <Toolbar sx={{
                  minHeight: '56px',
                display: { xs: 'flex', sm: 'none' },
                justifyContent: 'space-between',
                px: { xs: 1, sm: 2 },
                bgcolor: 'background.paper',
                borderBottom: '1px solid',
                borderColor: 'divider',
                  position: 'fixed',
                top: 0,
                  left: shouldHideSidebar ? 0 : 0,
                  right: 0,
                width: '100%',
                zIndex: 1100
              }}>
                {!shouldHideSidebar && (
                  <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    edge="start"
                    onClick={handleDrawerToggle}
                    sx={{
                      mr: 1,
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <MenuIcon />
                  </IconButton>
                )}
                <Box sx={{ flexGrow: 1 }} />
              </Toolbar>
              </>
            )}

            <Box sx={{ 
              pt: (keycloak.authenticated && !isRegistrationInvitePage && !isPioAgentFullscreen)
                ? { xs: '56px', sm: '64px' } 
                : 0 
            }}>
              {!needsRedirect && (
              <Routes>
                <Route path="/" element={keycloak.authenticated ? <PrivateRoute><Home /></PrivateRoute> : getLandingPage()} />
                <Route path="/impressum" element={<Impressum />} />
                <Route path="/documentation" element={<Documentation />} />
                {/* <Route path="/register" element={<Registration />} /> */}
                <Route path="/user/v1/user/invitation/:token" element={<RegistrationInvite />} />

                <Route path="/home" element={<PrivateRoute><Home /></PrivateRoute>} />
                <Route path="/dashboard" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                <Route path="/cloud-management" element={<PrivateRoute><ProtectedRoute feature="cloudManagement"><CloudManagement title="Cloud Management" /></ProtectedRoute></PrivateRoute>} />
                <Route path="/discover-instances" element={<PrivateRoute><DiscoverInstances /></PrivateRoute>} />
                <Route path="/apphub" element={<PrivateRoute><AppHub /></PrivateRoute>} />
                <Route path="/app-preview" element={<PrivateRoute><AppHub /></PrivateRoute>} />
                <Route path="/apphub" element={<PrivateRoute><AppHub /></PrivateRoute>} />
                <Route path="/meeting-summarizer" element={<PrivateRoute><MeetingSummarizer /></PrivateRoute>} />
                <Route path="/hashcat" element={<PrivateRoute><HashCat /></PrivateRoute>} />
                <Route path="/paas" element={<PrivateRoute><PaaS /></PrivateRoute>} />
                <Route path="/callbot" element={<PrivateRoute><Callbot /></PrivateRoute>} />
                <Route path="/piowidget" element={<PrivateRoute><PioWidget /></PrivateRoute>} />
                <Route path="/monitoring/:serverId" element={<PrivateRoute><ProtectedRoute feature="nodeMonitoring"><NodeMonitoring /></ProtectedRoute></PrivateRoute>} />
                <Route path="/test" element={<PrivateRoute><Test /></PrivateRoute>} />
                <Route path="/agent-studio" element={<PrivateRoute><AgentStudio /></PrivateRoute>} />
                <Route path="/api-keys/:serverId" element={<PrivateRoute><KeyManager /></PrivateRoute>} />
                <Route path="/rag-upload/:serverId" element={<PrivateRoute><RAGUpload /></PrivateRoute>} />
                <Route path="/chat-agent" element={<PrivateRoute><ChatAgent /></PrivateRoute>} />
                <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
                <Route path="/agents/:agentId/manage" element={<PrivateRoute><ManageAgents /></PrivateRoute>} />
                <Route path="/admin" element={<PrivateRoute><ProtectedRoute requireAdmin={true}><AdminPage /></ProtectedRoute></PrivateRoute>} />
                <Route path="/pioagent/chat" element={<PrivateRoute><PioAgentUserProvider><ChatPage /></PioAgentUserProvider></PrivateRoute>} />
                <Route path="/pioagent/api-config" element={<PrivateRoute><PioAgentUserProvider><ApiConfigPage /></PioAgentUserProvider></PrivateRoute>} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              )}
            </Box>
          </Box>
        </Box>
      </ThemeProvider>
  );
}

function AppContent() {
  return (
    <UserProvider>
      <AppContentInner />
    </UserProvider>
  );
}

function App() {
  return (
    <ReactKeycloakProvider
      authClient={keycloak}
      initOptions={initOptions}
    >
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AppContent />
      </Router>
    </ReactKeycloakProvider>
  );
}

export default App;
